import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { getUtilityIcon } from 'utils/utilityTypeUtils/utilityTypeUtils';
import { numericFormatter } from 'react-number-format';
// import i18next from 'i18next';
import Tippy from '@tippyjs/react';
import { TableProgressBar } from 'pages/Management/ProjectListPage/components';
import { ProjectStatusPill } from './ProjectStatusPill';
import { formatDecimalSeparator } from 'utils/formatters/number/numberUtils';
import { removeUnderscoreAndCapitalize } from 'utils/formatters/string/stringFormatter';
import { useTranslation } from 'react-i18next';

interface AnomaliesTableColumnsReturnType {
  columns: ColumnDef<any, any>[];
}

const useProjectsNeedAttentionColumns = (): AnomaliesTableColumnsReturnType => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<any>();

  const columns = [
    columnHelper.accessor('name', {
      header: () =>
        t('buildingView.building_need_attention_table.columns.name'),
      cell: (info) => {
        const values = info.row.original;
        const label =
          values.analytic_type === 'structure_optimization'
            ? removeUnderscoreAndCapitalize(info.renderValue())
            : info.renderValue();
        return (
          <Tippy content={info.getValue()} maxWidth={450}>
            <span className="flex items-center gap-2.5">
              <div className="fill-gray-50">
                {getUtilityIcon(values.utility_type)}
              </div>
              <span className="max-w-[300px] desktop-l:max-w-[400px] block text-ellipsis overflow-hidden truncate">
                {label}
              </span>
            </span>
          </Tippy>
        );
      },
      enableSorting: true,
      size: 200,
      maxSize: 200,
    }),
    columnHelper.accessor('project_status', {
      id: 'status',
      header: () =>
        t('buildingView.building_need_attention_table.columns.status'),
      cell: (info) => {
        return <ProjectStatusPill status={info.getValue()} />;
      },
    }),
    columnHelper.accessor('investment_cost', {
      header: () =>
        t('buildingView.building_need_attention_table.columns.investment'),
      cell: (info) => {
        return `${numericFormatter((info.getValue() ?? 0).toFixed(2), {
          decimalSeparator: ',',
          thousandSeparator: '.',
          decimalScale: 2,
        })} €`;
      },
    }),
    columnHelper.accessor('savings_euro', {
      header: () =>
        t(
          'buildingView.building_need_attention_table.columns.costSavingsPerYear'
        ),
      cell: (info) => {
        const values = info.row.original;
        const isSuggestedProject =
          values.analytic_type === 'structure_optimization';
        const value = isSuggestedProject
          ? values.calculation_details.savings_euro_per_year
          : info.getValue();
        return `${numericFormatter((value ?? 0).toFixed(2), {
          decimalSeparator: ',',
          thousandSeparator: '.',
          decimalScale: 2,
        })} €`;
      },
    }),
    columnHelper.accessor('payback_time_years', {
      header: () =>
        t('buildingView.building_need_attention_table.columns.payback'),
      cell: (info) => {
        const payback = info.getValue();
        return payback
          ? formatDecimalSeparator({
              value: Math.abs(payback ?? 0),
              decimalScale: 1,
            }) + ' years'
          : '-';
      },
    }),
    columnHelper.accessor('distance_from_target', {
      id: 'distance_from_target',
      header: () =>
        t('buildingView.building_need_attention_table.columns.target'),
      cell: (info) => {
        const values = info.row.original;
        const isSuggestedProject =
          values.analytic_type === 'structure_optimization';

        const completed = parseFloat(
          numericFormatter(((values?.savings_rel ?? 0) * 100).toString(), {
            decimalSeparator: ',',
            thousandSeparator: '.',
            decimalScale: 1,
          })
        );
        const maxCompleted = parseFloat(
          numericFormatter(
            (
              ((isSuggestedProject
                ? values?.savings_rel
                : values?.savings_target) ?? 0) * 100
            ).toString(),
            {
              decimalSeparator: ',',
              thousandSeparator: '.',
              decimalScale: 1,
            }
          )
        );
        return (
          <TableProgressBar
            completed={completed}
            hideCompletedNumber={isSuggestedProject}
            maxCompleted={maxCompleted}
            maxWidth={120}
            enableNegativeBar={false}
          />
        );
      },
    }),
  ];

  return {
    columns,
  };
};
export default useProjectsNeedAttentionColumns;
